"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CloudRain, Droplets, Sun, Bug, ShieldCheck, Shield, CircleCheck, CircleX } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const diyIssues = [{
  title: "Heavy Rain Washout",
  description: "Store-bought surface sprays are quickly washed away by Punta Gorda's daily summer thunderstorms, leaving your home unprotected.",
  icon: CloudRain
}, {
  title: "High Water Table Dilution",
  description: "Shallow soil moisture in coastal Florida dilutes granular DIY treatments, rendering them completely ineffective against subterranean threats.",
  icon: Droplets
}, {
  title: "Extreme Heat Degradation",
  description: "Intense UV rays and 90°+ heat break down the active ingredients in amateur pest products within days of application.",
  icon: Sun
}, {
  title: "Hidden Termite Swarms",
  description: "Subterranean termites thrive in our humid soil, easily bypassing weak, surface-level DIY chemical barriers.",
  icon: Bug
}];
const proSolutions = [{
  title: "Weather-Resistant Bonding",
  description: "We utilize advanced micro-encapsulated formulas that bind tightly to surfaces, withstanding heavy rain and harsh weather.",
  icon: ShieldCheck
}, {
  title: "Deep-Soil Injection",
  description: "Professional trenching and rodding techniques reach below the water table to establish a solid defensive perimeter.",
  icon: Shield
}, {
  title: "UV-Stable Treatments",
  description: "Commercial-grade, FDACS-approved products designed specifically to maintain maximum efficacy under the intense Florida sun.",
  icon: CircleCheck
}, {
  title: "Comprehensive Eradication",
  description: "Advanced baiting systems and targeted applications that actively eliminate entire colonies at the source, not just the stragglers.",
  icon: ShieldCheck
}];
export default function ClimateResistantComparison() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="climateresistantcomparison" className="w-full overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 w-full min-h-screen">
        
        {}
        <div ref={leftRef} className={`bg-primary text-primary-foreground p-8 md:p-16 lg:p-24 flex flex-col justify-center transition-all duration-1000 ease-out ${leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
          <div className="max-w-xl mx-auto w-full">
            <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold mb-16 leading-tight">
              The Cost of <br /><span className="text-destructive">DIY Failure</span>
            </h2>
            
            <div className="flex flex-col gap-12">
              {diyIssues.map((item, i) => <Card key={i} data-index={i} className="bg-transparent border-none shadow-none rounded-none p-0">
                  <CardHeader className="p-0 mb-4 flex flex-row items-center gap-4 space-y-0">
                    <div className="bg-destructive/20 p-3 rounded-none">
                      <item.icon className="w-8 h-8 text-destructive" />
                    </div>
                    <CardTitle className="font-default text-2xl text-primary-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <p className="font-serif text-lg md:text-xl leading-relaxed text-primary-foreground/80">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>)}
            </div>
          </div>
        </div>

        {}
        <div ref={rightRef} className={`bg-background text-foreground p-8 md:p-16 lg:p-24 flex flex-col justify-center transition-all duration-1000 ease-out delay-300 ${rightVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
          <div className="max-w-xl mx-auto w-full">
            <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold mb-16 leading-tight">
              The Power of <br />Professional Solutions
            </h2>
            
            <div className="flex flex-col gap-12 mb-16">
              {proSolutions.map((item, i) => <Card key={i} data-index={i} className="bg-transparent border-none shadow-none rounded-none p-0">
                  <CardHeader className="p-0 mb-4 flex flex-row items-center gap-4 space-y-0">
                    <div className="bg-primary/10 p-3 rounded-none">
                      <item.icon className="w-8 h-8 text-primary" />
                    </div>
                    <CardTitle className="font-default text-2xl text-primary">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <p className="font-sans text-lg md:text-xl leading-relaxed text-foreground/80">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>)}
            </div>

            <div className="flex justify-center lg:justify-start mt-8">
              <Link to="/ContactPage">
                <Button size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 text-lg px-10 py-8 h-auto font-default uppercase tracking-wider shadow-xl">
                  Schedule Free Inspection
                </Button>
              </Link>
            </div>
          </div>
        </div>

      </div>
    </section>;
}